<template>
  <div class="bg-n-slate-2 dark:bg-n-slate-3 rounded-xl shadow-sm border border-n-weak">
    <div class="px-6 py-4 border-b border-n-weak">
      <h3 class="text-lg font-semibold text-n-slate-12">
        ⏱️ Timeline de Atribuições ({{ timeline.length }})
      </h3>
      <p class="mt-1 text-xs text-n-slate-10">
        Últimas conversas atribuídas pelo Round Robin
      </p>
    </div>

    <div class="overflow-y-auto" style="max-height: 500px;">
      <div v-if="timeline && timeline.length > 0" class="p-6 space-y-3">
        <div
          v-for="item in timeline"
          :key="item.id"
          class="flex items-center gap-4 p-4 rounded-lg border transition-colors"
          :class="{
            'bg-n-slate-1 dark:bg-n-slate-2 border-n-weak hover:border-n-blue-7 dark:hover:border-n-blue-8': !isItemSelected(item),
            'bg-n-blue-3 dark:bg-n-blue-4 border-n-blue-9 border-2 ring-2 ring-n-blue-9 ring-offset-2 dark:ring-offset-n-slate-3': isItemSelected(item)
          }"
        >
          <!-- Avatar -->
          <div class="flex-shrink-0">
            <img
              v-if="item.assignee.avatar_url"
              :src="item.assignee.avatar_url"
              :alt="item.assignee.name"
              class="h-10 w-10 rounded-full"
            />
            <div
              v-else
              class="h-10 w-10 rounded-full bg-gradient-to-br from-n-blue-9 to-n-iris-9 flex items-center justify-center text-white text-sm font-semibold"
            >
              {{ getInitials(item.assignee.name) }}
            </div>
          </div>

          <!-- Info -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ item.assignee.name }}
              </span>
              <span class="text-xs text-n-slate-10">→</span>
              <div class="flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-n-slate-10">
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span class="text-xs text-n-slate-11">
                  {{ item.contact.name }}
                </span>
              </div>
            </div>
            <div class="flex items-center gap-3 text-xs text-n-slate-10">
              <span>{{ item.time_ago }}</span>
            </div>
          </div>

          <!-- Status Badge -->
          <div class="flex-shrink-0">
            <span
              class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
              :class="getStatusClass(item.status)"
            >
              {{ getStatusLabel(item.status) }}
            </span>
          </div>

          <!-- Priority Badge (if exists) -->
          <div v-if="item.priority" class="flex-shrink-0">
            <span
              class="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
              :class="getPriorityClass(item.priority)"
            >
              {{ getPriorityIcon(item.priority) }}
            </span>
          </div>

          <!-- Go to Inbox Button -->
          <div class="flex-shrink-0">
            <button
              v-tooltip="'Abrir na caixa de entrada'"
              class="inline-flex items-center justify-center h-8 w-8 rounded-lg text-n-blue-11 hover:text-n-blue-12 hover:bg-n-blue-3 dark:hover:bg-n-blue-4 transition-colors"
              @click.stop="navigateToConversation(item.display_id)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-12">
        <div class="text-4xl mb-4">📭</div>
        <p class="text-n-slate-11">
          Nenhuma atribuição no período selecionado
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { computed } from 'vue';

export default {
  name: 'TimelineList',
  props: {
    timeline: {
      type: Array,
      default: () => [],
    },
    selectedAgentId: {
      type: Number,
      default: null,
    },
  },
  setup() {
    const router = useRouter();
    const currentAccountId = useMapGetter('getCurrentAccountId');
    const accountId = computed(() => currentAccountId.value);

    const navigateToConversation = (displayId) => {
      if (!displayId) {
        console.error('[TimelineList] display_id é obrigatório');
        return;
      }

      router.push({
        name: 'inbox_conversation',
        params: {
          accountId: accountId.value,
          conversation_id: displayId,
        },
      });
    };

    return {
      navigateToConversation,
    };
  },
  methods: {
    isItemSelected(item) {
      if (!this.selectedAgentId) return false;
      return item.assignee.id === this.selectedAgentId;
    },
    getInitials(name) {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    getStatusClass(status) {
      const classes = {
        open: 'bg-n-blue-3 text-n-blue-11 dark:bg-n-blue-4 dark:text-n-blue-10',
        pending: 'bg-n-amber-3 text-n-amber-11 dark:bg-n-amber-4 dark:text-n-amber-10',
        resolved: 'bg-n-teal-3 text-n-teal-11 dark:bg-n-teal-4 dark:text-n-teal-10',
        snoozed: 'bg-n-slate-3 text-n-slate-11 dark:bg-n-slate-5 dark:text-n-slate-10',
      };
      return classes[status] || classes.open;
    },
    getStatusLabel(status) {
      const labels = {
        open: 'Aberta',
        pending: 'Pendente',
        resolved: 'Resolvida',
        snoozed: 'Adiada',
      };
      return labels[status] || status;
    },
    getPriorityClass(priority) {
      const classes = {
        urgent: 'bg-n-ruby-3 text-n-ruby-11 dark:bg-n-ruby-4 dark:text-n-ruby-10',
        high: 'bg-n-amber-3 text-n-amber-11 dark:bg-n-amber-4 dark:text-n-amber-10',
        medium: 'bg-n-amber-2 text-n-amber-11 dark:bg-n-amber-3 dark:text-n-amber-10',
        low: 'bg-n-slate-3 text-n-slate-11 dark:bg-n-slate-5 dark:text-n-slate-10',
      };
      return classes[priority] || classes.medium;
    },
    getPriorityIcon(priority) {
      const icons = {
        urgent: '🔴',
        high: '🟠',
        medium: '🟡',
        low: '⚪',
      };
      return icons[priority] || '⚪';
    },
  },
};
</script>